// src/pages/IntakeRequests.jsx
// Phase E1 — Accountant side: create information requests for clients and
// track checklist completion in real time.
import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { Inbox, ChevronDown, ChevronUp, Download, FileText, MessageSquare, Clock } from 'lucide-react'
import { getClients, createIntakeRequest, getIntakeRequests, sendIntakeReminder, getIntakeDocument } from '../api/kailux'
import EmptyState from '../components/EmptyState'
import Spinner from '../components/Spinner'

import Icon from '../components/Icon'

import { T, alpha } from '../theme/theme'
const COMMON_ITEMS = [
  { id:'fixed_asset_register', label:'Fixed asset register (Excel/CSV)', type:'file' },
  { id:'loan_agreements',       label:'Loan agreements / amortisation schedules', type:'file' },
  { id:'lease_agreements',      label:'Lease agreements', type:'file' },
  { id:'prior_year_afs',        label:'Prior year signed AFS', type:'file' },
  { id:'age_analysis',          label:'Debtors / creditors age analysis', type:'file' },
  { id:'vat201',                label:'VAT201 returns for the year', type:'file' },
  { id:'emp201_501',            label:'EMP201 / EMP501 submissions', type:'file' },
  { id:'cipc_docs',             label:'CIPC documents (incorporation, share register)', type:'file' },
  { id:'minutes',               label:'Minutes of meetings during the year', type:'file' },
  { id:'bank_confirmation',     label:'Bank confirmation letter', type:'file' },
  { id:'director_ids',          label:'Director ID copies', type:'file' },
  { id:'related_party_q',       label:'Were there any related party loans during the year?', type:'text' },
]

const STATUS_COLOR = { sent: T.bl, in_progress: T.or, completed: T.gn }
const STATUS_LABEL = { sent: 'Sent', in_progress: 'In progress', completed: 'Completed' }

export default function IntakeRequests() {
  const { firm } = useAuth()
  const [clients, setClients]   = useState([])
  const [requests, setRequests] = useState([])
  const [loading, setLoading]   = useState(true)
  const [showForm, setShowForm] = useState(false)

  // form state
  const [clientId, setClientId]       = useState('')
  const [clientName, setClientName]   = useState('')
  const [clientEmail, setClientEmail] = useState('')
  const [yearEnd, setYearEnd]         = useState('')
  const [selected, setSelected]       = useState({}) // {item_id: true}
  const [customItems, setCustomItems] = useState([])
  const [customLabel, setCustomLabel] = useState('')
  const [customType, setCustomType]   = useState('file')
  const [submitting, setSubmitting]   = useState(false)
  const [formError, setFormError]     = useState('')

  // FIX (Aug 2026 QA connector pass): the backend endpoint
  // (GET /api/intake/{id}/document/{itemId}) and the API client wrapper
  // (getIntakeDocument in api/kailux.js) both already existed and worked
  // correctly, but nothing in this file — or anywhere else in the
  // frontend — ever called getIntakeDocument. A firm could create a
  // request, a client could dutifully upload a document via the portal,
  // the request would show "completed" with a full progress bar, and the
  // accountant had no way anywhere in the app to actually retrieve what
  // was submitted. This is the missing wiring: an expandable per-request
  // panel listing each checklist item's submission (file download or
  // inline text), backed by the endpoint that was already there.
  const [expandedId, setExpandedId]     = useState(null)
  const [downloadingKey, setDownloadingKey] = useState(null) // `${reqId}:${itemId}`
  const [docError, setDocError]         = useState(null)     // `${reqId}:${itemId}` that last failed

  const load = () => {
    if (!firm?.id) return
    Promise.all([getClients(firm.id), getIntakeRequests({ firm_id: firm.id })])
      .then(([c, r]) => { setClients(c.data || []); setRequests(r.data || []) })
      .catch(console.error)
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [firm?.id])

  const toggleSelected = (id) => setSelected(s => ({ ...s, [id]: !s[id] }))

  const addCustom = () => {
    if (!customLabel.trim()) return
    setCustomItems(items => [...items, {
      id: `custom_${Date.now()}`, label: customLabel.trim(), type: customType, required: true,
    }])
    setCustomLabel('')
  }

  const removeCustom = (id) => setCustomItems(items => items.filter(i => i.id !== id))

  const resetForm = () => {
    setClientId(''); setClientName(''); setClientEmail(''); setYearEnd('')
    setSelected({}); setCustomItems([]); setCustomLabel(''); setFormError('')
  }

  const handleClientChange = (id) => {
    setClientId(id)
    const c = clients.find(c => c.id === id)
    setClientName(c?.name || '')
  }

  const handleSubmit = async () => {
    setFormError('')
    if (!clientId) return setFormError('Please select a client')
    if (!clientEmail || !clientEmail.includes('@')) return setFormError('Please enter a valid client email')

    const checklist = [
      ...COMMON_ITEMS.filter(i => selected[i.id]).map(i => ({ ...i, required: true })),
      ...customItems,
    ]
    if (checklist.length === 0) return setFormError('Select at least one checklist item')

    setSubmitting(true)
    try {
      await createIntakeRequest({
        firm_id: firm.id, client_id: clientId,
        client_name: clientName, client_email: clientEmail,
        firm_name: firm.name || '', year_end: yearEnd,
        checklist,
      })
      resetForm()
      setShowForm(false)
      load()
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Could not create the request — please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const remind = async (req) => {
    try {
      const res = await sendIntakeReminder(req.id)
      alert(`Reminder sent — ${res.data.outstanding} item(s) still outstanding.`)
      load()
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not send reminder.')
    }
  }

  const viewDocument = async (reqId, itemId) => {
    const key = `${reqId}:${itemId}`
    setDownloadingKey(key)
    setDocError(null)
    try {
      const res = await getIntakeDocument(reqId, itemId)
      // Opened in a new tab rather than navigated to directly: the URL is a
      // short-lived (5 minute) signed link, and opening in-place would
      // strand the accountant off this page for something that's meant to
      // be a quick look, not a navigation.
      window.open(res.data.url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      setDocError(key)
      alert(err.response?.data?.detail || 'Could not open this document — the link may have expired. Try again.')
    } finally {
      setDownloadingKey(null)
    }
  }

  return (
    <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"'Outfit',sans-serif",
      color:T.tx, padding:'32px' }}>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'24px' }}>
        <div>
          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:'22px', fontWeight:'800' }}>
            Client Information Requests
          </div>
          <div style={{ fontSize:'13px', fontWeight:'400', color:T.mt, marginTop:'4px' }}>
            Ask clients for documents and information — they upload via a secure link, no login required
          </div>
        </div>
        <button onClick={() => setShowForm(s => !s)}
          style={{ padding:'10px 22px', borderRadius:'8px', border:'none',
            background:T.prg, color:T.onpri,
            fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
          {showForm ? 'Cancel' : '+ New Request'}
        </button>
      </div>

      {showForm && (
        <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px',
          padding:'24px', marginBottom:'28px' }}>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px', marginBottom:'18px' }}>
            <div>
              <label style={{ fontSize:'12px', color:T.mt, display:'block', marginBottom:'6px' }}>Client</label>
              <select value={clientId} onChange={e => handleClientChange(e.target.value)}
                style={{ width:'100%', padding:'10px 12px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                  background:T.s2, color:T.tx, fontFamily:"'Outfit',sans-serif", fontSize:'14px' }}>
                <option value="">Select client…</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize:'12px', color:T.mt, display:'block', marginBottom:'6px' }}>Client email</label>
              <input value={clientEmail} onChange={e => setClientEmail(e.target.value)}
                placeholder="director@client.co.za"
                style={{ width:'100%', padding:'10px 12px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                  background:T.s2, color:T.tx, fontFamily:"'Outfit',sans-serif", fontSize:'14px' }} />
            </div>
            <div>
              <label style={{ fontSize:'12px', color:T.mt, display:'block', marginBottom:'6px' }}>Financial year end (optional)</label>
              <input value={yearEnd} onChange={e => setYearEnd(e.target.value)}
                placeholder="28 February 2026"
                style={{ width:'100%', padding:'10px 12px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                  background:T.s2, color:T.tx, fontFamily:"'Outfit',sans-serif", fontSize:'14px' }} />
            </div>
          </div>

          <div style={{ fontSize:'13px', fontWeight:'700', color:T.lt, marginBottom:'10px' }}>
            Select items to request
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'18px' }}>
            {COMMON_ITEMS.map(item => (
              <label key={item.id} onClick={() => toggleSelected(item.id)}
                style={{ display:'flex', alignItems:'center', gap:'8px', padding:'10px 12px',
                  borderRadius:'8px', cursor:'pointer',
                  border:`1.5px solid ${selected[item.id] ? T.or : T.bd}`,
                  background: selected[item.id] ? T.ord : T.s2 }}>
                <div style={{ width:'16px', height:'16px', borderRadius:'6px', flexShrink:0,
                  border:`1.5px solid ${selected[item.id] ? T.or : T.mt}`,
                  background: selected[item.id] ? T.or : 'transparent',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:'12px', color:T.onpri }}>
                  {selected[item.id] && <Icon name="check" />}
                </div>
                <span style={{ fontSize:'13px' }}>{item.label}</span>
                <span style={{ fontSize:'10px', color:T.mt, marginLeft:'auto' }}>
                  {item.type === 'text' ? 'Question' : 'Upload'}
                </span>
              </label>
            ))}
          </div>

          <div style={{ fontSize:'13px', fontWeight:'700', color:T.lt, marginBottom:'10px' }}>
            Custom items
          </div>
          {customItems.length > 0 && (
            <div style={{ display:'flex', flexDirection:'column', gap:'4px', marginBottom:'10px' }}>
              {customItems.map(item => (
                <div key={item.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                  padding:'8px 12px', borderRadius:'8px', border:`1px solid ${T.bd}`, background:T.s2 }}>
                  <span style={{ fontSize:'13px' }}>{item.label} <span style={{ color:T.mt }}>({item.type === 'text' ? 'Question' : 'Upload'})</span></span>
                  <button onClick={() => removeCustom(item.id)}
                    style={{ background:'none', border:'none', color:T.mt, cursor:'pointer', fontSize:'14px' }}><Icon name="x" /></button>
                </div>
              ))}
            </div>
          )}
          <div style={{ display:'flex', gap:'8px' }}>
            <input value={customLabel} onChange={e => setCustomLabel(e.target.value)}
              placeholder="e.g. Signed shareholder resolution for dividend"
              style={{ flex:1, padding:'10px 12px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                background:T.s2, color:T.tx, fontFamily:"'Outfit',sans-serif", fontSize:'14px' }} />
            <select value={customType} onChange={e => setCustomType(e.target.value)}
              style={{ padding:'10px 12px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                background:T.s2, color:T.tx, fontFamily:"'Outfit',sans-serif", fontSize:'14px' }}>
              <option value="file">Upload</option>
              <option value="text">Question (text answer)</option>
            </select>
            <button onClick={addCustom}
              style={{ padding:'10px 18px', borderRadius:'8px', border:`1px solid ${T.bd}`,
                background:'transparent', color:T.tx, fontFamily:"'Outfit',sans-serif",
                fontSize:'14px', fontWeight:'700', cursor:'pointer' }}>
              + Add
            </button>
          </div>

          {formError && (
            <div style={{ marginTop:'16px', padding:'10px 14px', borderRadius:'8px',
              background:alpha(T.rd, 0.1), border:`1px solid ${alpha(T.rd, 0.25)}`,
              color:T.rd, fontSize:'13px' }}>
              {formError}
            </div>
          )}

          <button onClick={handleSubmit} disabled={submitting}
            style={{ marginTop:'18px', padding:'12px 28px', borderRadius:'8px', border:'none',
              background:T.prg, color:T.onpri,
              fontFamily:"'Outfit',sans-serif", fontSize:'14px', fontWeight:'700',
              cursor: submitting ? 'default' : 'pointer' }}>
            {submitting ? 'Sending…' : 'Send to client →'}
          </button>
        </div>
      )}

      <div style={{ background:T.s1, border:`1px solid ${T.bd}`, borderRadius:'12px', overflow:'hidden' }}>
        <div style={{ padding:'16px 20px', borderBottom:`1px solid ${T.bd}`, fontSize:'14px', fontWeight:'700' }}>
          All requests
        </div>
        {loading ? (
          <div style={{ padding:'32px', textAlign:'center' }}><Spinner fontSize='14px'/></div>
        ) : requests.length === 0 ? (
          <EmptyState icon={Inbox} title="No requests yet"
            subtitle="Create your first information request to get documents from a client" />
        ) : requests.map((req, i) => {
          const pct = req.total_items ? Math.round((req.completed_items / req.total_items) * 100) : 0
          const isExpanded = expandedId === req.id
          return (
            <div key={req.id} style={{ borderBottom: i < requests.length-1 ? `1px solid ${T.bd}` : 'none' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 20px' }}>
                <div style={{ flex:1, minWidth:0, cursor:'pointer' }}
                  onClick={() => setExpandedId(isExpanded ? null : req.id)}>
                  <div style={{ fontSize:'14px', fontWeight:'700' }}>{req.client_name}</div>
                  <div style={{ fontSize:'12px', color:T.mt, marginTop:'2px' }}>
                    {req.meta?.client_email} · {req.completed_items}/{req.total_items} items
                    {req.meta?.year_end && <> · Year end: {req.meta.year_end}</>}
                  </div>
                  <div style={{ height:'5px', borderRadius:'6px', background:T.dm, marginTop:'8px', maxWidth:'240px' }}>
                    <div style={{ height:'100%', borderRadius:'6px', width:`${pct}%`,
                      background: req.status==='completed' ? T.gn : T.or, transition:'width 0.3s' }} />
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'8px', marginLeft:'16px', flexShrink:0 }}>
                  <span style={{ fontSize:'12px', fontWeight:'700', padding:'4px 10px', borderRadius:'6px',
                    color: STATUS_COLOR[req.status] || T.mt,
                    background: alpha(STATUS_COLOR[req.status] || T.mt, 0.1) }}>
                    {STATUS_LABEL[req.status] || req.status}
                  </span>
                  {req.status !== 'completed' && (
                    <button onClick={() => remind(req)}
                      style={{ padding:'7px 14px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                        background:'transparent', color:T.mt, fontFamily:"'Outfit',sans-serif",
                        fontSize:'12px', fontWeight:'600', cursor:'pointer' }}>
                      <Icon name="bell" /> Remind
                    </button>
                  )}
                  <button onClick={() => setExpandedId(isExpanded ? null : req.id)}
                    aria-label={isExpanded ? 'Collapse' : 'View submitted items'}
                    style={{ padding:'7px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                      background:'transparent', color:T.mt, cursor:'pointer', display:'flex' }}>
                    {isExpanded ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                  </button>
                </div>
              </div>

              {isExpanded && (
                <div style={{ padding:'4px 20px 18px 20px', background:T.dm }}>
                  {(req.checklist || []).map(item => {
                    const resp = req.responses?.[item.id]
                    const key = `${req.id}:${item.id}`
                    const isDownloading = downloadingKey === key
                    return (
                      <div key={item.id} style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between',
                        gap:'12px', padding:'10px 0', borderBottom:`1px solid ${T.bd}` }}>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ fontSize:'13px', fontWeight:'600' }}>{item.label}</div>
                          {resp && item.type === 'text' && (
                            <div style={{ fontSize:'12px', color:T.mt, marginTop:'4px', whiteSpace:'pre-wrap' }}>
                              {resp.text}
                            </div>
                          )}
                          {!resp && (
                            <div style={{ fontSize:'12px', color:T.mt, marginTop:'4px', display:'flex',
                              alignItems:'center', gap:'4px' }}>
                              <Clock size={12}/> Not yet submitted
                            </div>
                          )}
                        </div>
                        {resp && item.type === 'file' && (
                          <button onClick={() => viewDocument(req.id, item.id)} disabled={isDownloading}
                            style={{ padding:'6px 12px', borderRadius:'6px', border:`1px solid ${T.bd}`,
                              background:'transparent', color: T.or, fontFamily:"'Outfit',sans-serif",
                              fontSize:'12px', fontWeight:'600', cursor: isDownloading ? 'default' : 'pointer',
                              display:'flex', alignItems:'center', gap:'6px', flexShrink:0, whiteSpace:'nowrap' }}>
                            {isDownloading ? <Spinner fontSize='11px'/> : <Download size={13}/>}
                            {resp.file_name || 'Download'}
                          </button>
                        )}
                        {resp && item.type === 'text' && (
                          <MessageSquare size={14} color={T.mt} style={{ flexShrink:0, marginTop:'2px' }}/>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
